import { AxiosError } from 'axios';
import { axiosInstance } from './axiosInstance';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    Comment,
    BrandVoiceGraph,
    FollowersGraph,
    WeeklyStats,
} from '../../shared/types/dashboardTypes';

export const fetchLatestComments = createAsyncThunk<
    Comment[],
    { platform: string; startDate?: string; endDate?: string },
    { rejectValue: string }
>('projects/fetchLatestComments', async ({ platform, startDate, endDate }, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams();
        params.append('filter[content_type]', platform);

        const response = await axiosInstance.get(`/dashboard/latest-comments?${params.toString()}`);

        return response.data.data.map((item: any) => ({
            id: item.id,
            author: item.attributes.author,
            text: item.attributes.text,
            provider: item.attributes.provider,
            post_id: item.attributes.post_id,
            created_at: item.attributes.created_at,
        }));
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(
                axiosError.response?.data.message || 'Failed to fetch latest comments',
            );
        }
        return rejectWithValue('An unexpected error occurred');
    }
});

export const fetchWeeklyStats = createAsyncThunk<
    WeeklyStats,
    { platform: string; startDate?: string; endDate?: string },
    { rejectValue: string }
>('dashboard/fetchWeeklyStats', async ({ platform, startDate, endDate }, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams();
        params.append('filter[content_type]', platform);

        if (startDate) {
            params.append('filter[created_at][gte]', startDate);
        }
        if (endDate) {
            params.append('filter[created_at][lte]', endDate);
        }

        const response = await axiosInstance.get(`/dashboard/weekly-stats?${params.toString()}`);

        return response.data.data.attributes;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const axiosError = error as AxiosError<{ message: string }>;
            return rejectWithValue(
                axiosError.response?.data.message || 'Failed to fetch weekly stats',
            );
        }
        return rejectWithValue('An unexpected error occurred');
    }
});

export const fetchBrandVoiceGraph = createAsyncThunk<
    BrandVoiceGraph,
    { platform: string; startDate?: string; endDate?: string },
    { rejectValue: string }
>(
    'dashboard/fetchBrandVoiceGraph',
    async ({ platform, startDate, endDate }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            params.append('filter[content_type]', platform);

            if (startDate) {
                params.append('filter[created_at][gte]', startDate);
            }
            if (endDate) {
                params.append('filter[created_at][lte]', endDate);
            }

            const response = await axiosInstance.get(
                `/dashboard/graph/brand-voice?${params.toString()}`,
            );

            return response.data.data.attributes;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(
                    axiosError.response?.data.message || 'Failed to fetch brand voice graph',
                );
            }
            return rejectWithValue('An unexpected error occurred');
        }
    },
);

export const fetchFollowersGraph = createAsyncThunk<
    FollowersGraph,
    { platform: string; startDate?: string; endDate?: string },
    { rejectValue: string }
>(
    'dashboard/fetchFollowersGraph',
    async ({ platform, startDate, endDate }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            params.append('filter[content_type]', platform);

            if (startDate) {
                params.append('filter[created_at][gte]', startDate);
            }
            if (endDate) {
                params.append('filter[created_at][lte]', endDate);
            }

            const response = await axiosInstance.get(
                `/dashboard/graph/followers?${params.toString()}`,
            );

            return response.data.data.attributes;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(
                    axiosError.response?.data.message || 'Failed to fetch followers graph',
                );
            }
            return rejectWithValue('An unexpected error occurred');
        }
    },
);
